import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import {
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ArrowRightStartOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useAuth, useChain, useStore, useAdminUI } from '@/stores';
import { setAuthToken } from '@/services/api/client';
import { LoginModal } from '@/components/common/LoginModal';
import { ROUTES } from '@/config/routes';
import { cn } from '@/utils/cn';
import { getWalletAddressForChain } from '@/utils/wallets';

interface ProfileDropdownProps {
  variant?: 'dropdown' | 'list';
}

export const ProfileDropdown = ({ variant = 'dropdown' }: ProfileDropdownProps) => {
  const { user, isAuthenticated } = useAuth();
  const { selectedChain } = useChain();
  const adminUI = useAdminUI();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const navigate = useNavigate();
  const walletAddress = getWalletAddressForChain(user, selectedChain);

  const handleLogout = () => {
    // Clear token from localStorage and API client
    setAuthToken(null);
    // Update Zustand store
    useStore.setState((state) => ({
      auth: {
        ...state.auth,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false
      }
    }));
  };

  // If not logged in, show login button
  if (!isAuthenticated || !user) {
    return (
      <>
        <button
          onClick={() => setIsLoginModalOpen(true)}
          className={cn(
            'flex items-center gap-1.5 rounded-lg border bg-white px-3 py-1.5 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50 dark:border-white/[0.07] dark:bg-white/[0.04] dark:text-gray-100 dark:hover:border-white/[0.14] dark:hover:bg-white/[0.08]',
            variant === 'list' && 'w-full justify-start px-3 py-2'
          )}
        >
          <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
          <span className={cn(variant === 'dropdown' && 'hidden sm:inline')}>Sign In</span>
        </button>
        <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
      </>
    );
  }

  const menuItems = [
    {
      label: 'My Wallet',
      icon: UserCircleIcon,
      action: () => navigate(`/data-explorer/${selectedChain}/wallet/${walletAddress}`),
      // Wallet-profile pages live in the data explorer — admin experience only.
      show: !!walletAddress && adminUI,
    },
    {
      label: 'Settings',
      icon: Cog6ToothIcon,
      action: () => navigate(ROUTES.SETTINGS),
      show: true,
    },
    {
      label: 'Sign Out',
      icon: ArrowRightOnRectangleIcon,
      action: handleLogout,
      danger: true,
      show: true,
    },
  ];

  if (variant === 'list') {
    return (
      <div className="space-y-1">
        <div className="mb-3 rounded-lg border p-3 dark:border-white/[0.08]">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-violet-500/80 to-purple-500/80 text-xs font-semibold text-white">
              {user.username.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 overflow-hidden">
              <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                {user.username}
              </div>
              {user.email && (
                <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                  {user.email}
                </div>
              )}
              {user.twitter && !user.email && (
                <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                  @{user.twitter}
                </div>
              )}
            </div>
          </div>
        </div>

        {menuItems
          .filter((item) => item.show)
          .map((item) => (
            <button
              key={item.label}
              onClick={item.action}
              className={cn(
                'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-gray-100 dark:hover:bg-white/[0.05]',
                'text-gray-900 dark:text-gray-100',
                item.danger && 'text-red-600 dark:text-red-400'
              )}
            >
              <item.icon className="h-5 w-5" />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
      </div>
    );
  }

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="flex items-center gap-1.5 rounded-lg border bg-white px-3 py-1.5 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50 dark:border-white/[0.07] dark:bg-white/[0.04] dark:text-gray-100 dark:hover:border-white/[0.14] dark:hover:bg-white/[0.08]">
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-violet-500/80 to-purple-500/80 text-xs font-semibold text-white">
          {user.username.charAt(0).toUpperCase()}
        </div>
        <span className="hidden sm:inline">{user.username}</span>
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-xl border bg-white shadow-lg focus:outline-none dark:border-white/[0.08] dark:bg-abyss-100/95 dark:shadow-card-soft dark:backdrop-blur-xl">
          {/* User info */}
          <div className="border-b p-3 dark:border-white/[0.08]">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-violet-500/80 to-purple-500/80 text-xs font-semibold text-white">
                {user.username.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 overflow-hidden">
                <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                  {user.username}
                </div>
                {user.email && (
                  <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                    {user.email}
                  </div>
                )}
                {user.twitter && !user.email && (
                  <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                    @{user.twitter}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Menu items */}
          <div className="p-1">
            {menuItems
              .filter((item) => item.show)
              .map((item) => (
                <Menu.Item key={item.label}>
                  {({ active }) => (
                    <button
                      onClick={item.action}
                      className={cn(
                        'group flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
                        active
                          ? 'bg-gray-100 dark:bg-white/[0.05]'
                          : 'text-gray-900 dark:text-gray-100',
                        item.danger && 'text-red-600 dark:text-red-400'
                      )}
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  )}
                </Menu.Item>
              ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};
